const KEY = "fm313.longplay.language.v1"
const LANGUAGES = new Set(["en", "es"])

export default class LongplayLanguage {
  constructor({ storage = null, origin = "" } = {}) {
    this.storage = storage
    this.origin = origin
    this.preferred = "en"
    this.pairs = {}
    this.states = {}
    this.current = null
    this.catalogLoaded = false
    try {
      const saved = storage?.getItem(KEY)
      if (LANGUAGES.has(saved)) this.preferred = saved
    } catch (_) {}
  }

  choose(language) {
    if (!LANGUAGES.has(language)) return false
    this.preferred = language
    try { this.storage?.setItem(KEY, language) } catch (_) {}
    return true
  }

  update(payload, programVersion) {
    if (payload?.enabled !== true || payload.program_version !== programVersion || !payload.pairs || typeof payload.pairs !== "object") return false
    const pairs = {}
    for (const [id, pair] of Object.entries(payload.pairs).slice(0, 128)) {
      if (!/^lps_[a-f0-9]{24}$/.test(id) || !(Number.isFinite(pair?.duration_seconds) && pair.duration_seconds >= 8 && pair.duration_seconds <= 150)) continue
      const valid = [...LANGUAGES].every(language => {
        try {
          const url = new URL(pair[language], this.origin)
          return url.origin === this.origin && new RegExp(`^/__longplay/languages/[a-f0-9]{64}/${language}$`).test(url.pathname) && !url.search && !url.hash
        } catch (_) { return false }
      })
      if (valid) pairs[id] = pair
    }
    this.pairs = pairs
    this.catalogLoaded = true
    this.states = Object.fromEntries(Object.entries(payload.states || {}).filter(([id, state]) =>
      /^lps_[a-f0-9]{24}$/.test(id) && ["ready", "preparing", "unavailable"].includes(state)))
    return true
  }

  source(item, { commit = false } = {}) {
    if (!item?.is_dj_segment) {
      if (commit) this.current = null
      return { url: item?.audio_url, language: null, fallback: false }
    }
    const id = item.dj_segment_id
    // A selection or newly-ready pair cannot replace a sentence in progress.
    if (commit && this.current?.id === id) return this.current
    const pair = this.pairs[id]
    const available = pair && Math.abs(Number(item.duration_seconds) - pair.duration_seconds) <= 0.000001
    const result = { id, url: available ? pair[this.preferred] : item.audio_url,
      language: available ? this.preferred : null, fallback: !available,
      preparing: !available && this.states[id] === "preparing" }
    if (commit) this.current = result
    return result
  }

  statusMessage(item) {
    const spanish = this.preferred === "es"
    const next = spanish ? "Español en la próxima pausa disponible." : "English from the next available break."
    if (!item?.is_dj_segment) return next
    const source = this.current?.id === item.dj_segment_id ? this.current : this.source(item)
    if (!source.fallback) {
      const listening = source.language === "es" ? "Escuchando en español." : "Listening in English."
      return source.language === this.preferred ? listening : `${listening} ${next}`
    }
    if (!this.catalogLoaded) return spanish
      ? "Suena la grabación original; comprobando idiomas."
      : "Original break playing; checking language options."
    // The verified pair may arrive after a sentence has started. Report that
    // accurately while keeping its committed original audio uninterrupted.
    if (!this.source(item).fallback) return spanish
      ? "Suena la grabación original. Español en la próxima pausa."
      : "Original break playing. English from the next break."
    if (this.states[item.dj_segment_id] === "preparing") return spanish
      ? "Versión en preparación; suena la grabación original."
      : "Language version preparing; original break playing."
    return spanish
      ? "Esta pausa no tiene versión en español; suena la grabación original."
      : "This break's language version is unavailable; original break playing."
  }
}
